/**
 * ReportPanel — WorkspacePanel wrapper for report sections, replacing the
 * old ReportCard + SectionHeader shells (DD-011 flat surfaces, DD-014
 * cockpit language). Collapse state is local: report sections don't
 * coordinate, you just open what you're reading. Every section starts
 * folded, showing its collapsedBody: the few figures that matter most, or
 * the one-line reason it has none. On the canvas page a ReportSlot decides
 * instead: always folded in the rail, always open in a tile, and open as
 * tall as its content in the phone's list.
 */

import React, { ReactNode, useContext, useLayoutEffect, useRef, useState } from "react";
import { WorkspacePanel } from "@/domains/Ideas/components/WorkspacePanel";
import { PanelHeaderAction } from "@/components/ui/PanelHeaderAction";
import { useMediaQuery } from "@/core/http/useMediaQuery";
import { ChartBarIcon, CloseIcon } from "@/components/icons";
import { cn } from "@/utils/cn";
import { describeChange, type ChangeTone } from "../utils/metricChange";
import { RailHeadlineContext, ReportSlotContext } from "../canvas/reportSlot";

/**
 * Standard empty body for a report section. Panels never hide when data
 * is missing — they say why it's missing instead.
 */
export const ReportPanelEmpty: React.FC<{ message: string; hint?: string }> = ({
  message,
  hint,
}) => (
  <div className="flex flex-col items-center justify-center py-10 text-center">
    <ChartBarIcon className="w-9 h-9 text-gray-300 mb-2.5" />
    <p className="text-sm text-gray-500">{message}</p>
    {hint && <p className="text-xs text-gray-400 mt-1">{hint}</p>}
  </div>
);

/** A folded section's one-line reason: why it has no figures. As tall as a
 *  headline figure (label + value lines), so a folded row stays level; in
 *  the rail, where the label moves to the header, as tall as the value. */
export const ReportPanelNote: React.FC<{ children: ReactNode }> = ({ children }) => {
  const inRail = useContext(ReportSlotContext)?.kind === "rail";
  return (
    <p className={cn("flex items-center text-[13px] text-gray-500", inRail ? "min-h-[28px]" : "min-h-[48px]")}>{children}</p>
  );
};

const TONE_TEXT: Record<ChangeTone, string> = {
  up: "text-green-600",
  down: "text-red-600",
  neutral: "text-gray-500",
};

const TONE_PILL: Record<ChangeTone, string> = {
  up: "bg-green-50 text-green-700",
  down: "bg-red-50 text-red-700",
  neutral: "bg-gray-100 text-gray-500",
};

/** A change as a soft tinted pill: "+12 (+5%)" in green, a fall in red.
 *  A change that rounds to zero is no pill, only a grey "no change". */
export const ChangePill: React.FC<{
  change: { value: number; percentage: number | null };
  formatChange?: (value: number) => string;
  /** Overrides the tone where lower is the win (CPC). */
  trend?: ChangeTone;
}> = ({ change, formatChange, trend }) => {
  const { label, tone } = describeChange(change, formatChange, trend);
  if (tone === undefined) {
    return <span className="shrink-0 text-[11px] text-gray-500 whitespace-nowrap">{label}</span>;
  }
  return (
    <span
      className={`inline-flex shrink-0 items-center rounded-full px-1.5 py-px text-[11px] font-medium tabular-nums ${
        TONE_PILL[tone]
      }`}
    >
      {label}
    </span>
  );
};

interface ReportStatProps {
  label: string;
  value: string;
  /** "lg" is a panel's headline figure; "md" one of its few key figures. */
  size?: "lg" | "md";
  /** Colours the value itself, for a figure that is a change (a rate). */
  tone?: ChangeTone;
  /** The pill after the value. */
  change?: { value: number; percentage: number | null };
  formatChange?: (value: number) => string;
  /** Overrides the pill's tone where lower is the win (CPC). */
  trend?: ChangeTone;
  /** Muted words after the value, such as "month so far". */
  note?: string;
}

// Sizes in px: the root font is 13px, so rem sizes would render at 13/16.
// A headline in the canvas's rail is a step smaller, so it keeps its
// sparkline beside it in 300px.
const VALUE_SIZE = { lg: "text-[26px] leading-8", md: "text-[18px] leading-6" };
const RAIL_LG = "text-[22px] leading-7";

/** A figure: a quiet label over the value, its change pill and a note.
 *  Flat, no tile (DD-002). */
export const ReportStat: React.FC<ReportStatProps> = ({
  label,
  value,
  size = "md",
  tone,
  change,
  formatChange,
  trend,
  note,
}) => {
  const inRail = useContext(ReportSlotContext)?.kind === "rail";
  // A rail item's headline label sits in its header beside the title, so the item is two lines.
  const labelInHeader = useContext(RailHeadlineContext) && size === "lg";
  const valueSize = inRail && size === "lg" ? RAIL_LG : VALUE_SIZE[size];
  return (
    <div className="min-w-0">
      {!labelInHeader && <p className="text-[12px] text-gray-500 truncate">{label}</p>}
      <p className="flex items-center gap-2 whitespace-nowrap">
        <span
          className={`min-w-0 truncate font-semibold tracking-tight ${valueSize} ${
            tone ? TONE_TEXT[tone] : "text-gray-900"
          }`}
        >
          {value}
        </span>
        {change && <ChangePill change={change} formatChange={formatChange} trend={trend} />}
        {note && <span className="shrink-0 text-[12px] text-gray-400">{note}</span>}
      </p>
    </div>
  );
};

/** A panel's key figures on one row, wrapping between figures. */
export const ReportStats: React.FC<{ children: ReactNode }> = ({ children }) => (
  <div className="flex flex-wrap items-end gap-x-8 gap-y-3">{children}</div>
);

/** A folded panel's body: its one headline figure, and a small chart of
 *  the same measure on the right when a series exists. `inline` keeps the
 *  chart next to the figure, for a full-width panel. */
export const ReportHeadline: React.FC<{ stat: ReactNode; chart?: ReactNode; inline?: boolean }> = ({
  stat,
  chart,
  inline = false,
}) => (
  <div
    className={`flex flex-wrap items-end gap-y-3 ${inline ? "gap-x-12" : "justify-between gap-x-4"}`}
  >
    <div className="min-w-0">{stat}</div>
    {chart && <div className="shrink-0">{chart}</div>}
  </div>
);

/** A tile's open body, filling the tile: the height its content leaves
 *  spare goes to the ChartBoxes inside, split between the rows they sit in,
 *  as `--chart-grow`. Measured with the charts at their own height, so a
 *  chart beside a taller list or a panel that loads more content reads
 *  right; a panel taller than its tile gets none and scrolls. */
const TileBody: React.FC<{ children: ReactNode }> = ({ children }) => {
  const ref = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const content = ref.current;
    const body = content?.parentElement;
    if (!content || !body) return;
    const measure = () => {
      const boxes = Array.from(content.querySelectorAll<HTMLElement>("[data-chart-grow]"));
      content.style.setProperty("--chart-grow", "0px");
      const rows = new Set(boxes.map((b) => Math.round(b.getBoundingClientRect().top))).size;
      const style = getComputedStyle(body);
      const room = body.clientHeight - parseFloat(style.paddingTop) - parseFloat(style.paddingBottom);
      const next = rows > 0 ? Math.max(0, Math.floor((room - content.offsetHeight) / rows)) : 0;
      content.style.setProperty("--chart-grow", `${next}px`);
    };
    const observer = new ResizeObserver(() => measure());
    observer.observe(body);
    observer.observe(content);
    measure();
    return () => observer.disconnect();
  }, []);
  return <div ref={ref}>{children}</div>;
};

interface ReportPanelProps {
  title: string;
  icon?: ReactNode;
  summary?: ReactNode;
  actions?: ReactNode;
  className?: string;
  /** What the folded section shows under its header. */
  collapsedBody?: ReactNode;
  /** The label of collapsedBody's headline figure. In the canvas's rail it
   *  moves into the header after the title, unless it repeats it, and the
   *  figure leaves its own out: "Website  Sessions" over "2,315 +5%". */
  headlineLabel?: string;
  children: ReactNode;
}

export const ReportPanel: React.FC<ReportPanelProps> = ({
  title,
  icon,
  summary,
  actions,
  className,
  collapsedBody,
  headlineLabel,
  children,
}) => {
  const slot = useContext(ReportSlotContext);
  const [collapsed, setCollapsed] = useState(true);
  // Below sm (40rem) the summary leaves the header row and wraps on a line
  // under it, so neither the title nor the summary truncates on a phone:
  // the subheader while open, above the figures while folded. At sm and up
  // no subheader is passed, so the panel renders no empty padded line. In
  // the canvas's tiles and the phone's list the panel is as narrow as a
  // phone's, so the summary goes under the title there too.
  const phone = !useMediaQuery("(min-width: 40rem)");
  const summaryBelow = (phone || slot != null) && summary != null;
  const summaryLine = summaryBelow ? (
    <div className="text-xs text-gray-400">{summary}</div>
  ) : undefined;
  if (slot?.kind === "inline") {
    // Open in the phone's list: as tall as its content, the page scrolls and
    // the panel never does. The whole header row folds it, at least 44px;
    // only the row, never the summary line under it.
    return (
      <WorkspacePanel
        title={title}
        icon={icon}
        subheader={summaryLine}
        actions={actions}
        collapsed={false}
        onToggleCollapse={slot.onFold}
        className={cn("[&>header>div:first-child]:h-[48px] [&>header>div>button]:self-stretch", className)}
      >
        {children}
      </WorkspacePanel>
    );
  }
  if (slot?.kind === "tile") {
    const close = slot.onClose ? (
      <PanelHeaderAction
        label="Close"
        icon={<CloseIcon className="w-4 h-4" />}
        variant="secondary"
        onClick={slot.onClose}
        ariaLabel={`Close ${title}`}
      />
    ) : null;
    // The whole tile, its charts taking the height the rest leaves; a panel
    // taller than its tile scrolls its body inside.
    return (
      <WorkspacePanel
        title={title}
        icon={icon}
        summary={summaryBelow ? undefined : summary}
        subheader={summaryLine}
        actions={actions || close ? <>{actions}{close}</> : undefined}
        collapsed={false}
        className={cn("h-full", className)}
      >
        <TileBody>{children}</TileBody>
      </WorkspacePanel>
    );
  }
  if (slot?.kind === "rail") {
    // A rail item is one glance in two lines: the title with the headline's
    // label, then the figure, its change and an 84px sparkline beside it.
    // The date line waits for the open panel, so about ten items fit a
    // laptop's rail.
    return (
      <WorkspacePanel
        title={title}
        icon={icon}
        summary={headlineLabel !== title ? headlineLabel : undefined}
        collapsed
        onToggleCollapse={slot.onOpen}
        collapsedBody={
          collapsedBody != null ? (
            <RailHeadlineContext.Provider value={headlineLabel != null}>
              {/* The rail is a container: a tablet's 280px rail takes a 64px sparkline, so it still sits beside the figure. */}
              <div className="[&_svg[role=img]]:w-[64px] @min-[296px]:[&_svg[role=img]]:w-[84px] [&_svg[role=img]]:h-auto">
                {collapsedBody}
              </div>
            </RailHeadlineContext.Provider>
          ) : null
        }
        // The title keeps its width and the label after it truncates: "Booking Intent  Link taps and…".
        className={cn("[&>header>div]:h-[34px] [&>div]:pb-[10px] [&_header_.font-semibold]:shrink-0", className)}
      >
        {children}
      </WorkspacePanel>
    );
  }
  return (
    <WorkspacePanel
      title={title}
      icon={icon}
      summary={summaryBelow ? undefined : summary}
      subheader={summaryLine}
      actions={actions}
      collapsed={collapsed}
      onToggleCollapse={() => setCollapsed((c) => !c)}
      collapsedBody={
        summaryLine ? (
          <>
            {summaryLine}
            {collapsedBody != null && <div className="mt-2">{collapsedBody}</div>}
          </>
        ) : (
          collapsedBody
        )
      }
      className={className}
    >
      {children}
    </WorkspacePanel>
  );
};
